import type { FavoriteTargetType } from "@compass/shared/schema/favorite";
import { TrendingUp } from "lucide-react";
import { buildFavoriteTarget } from "components/elements/ContentCard";
import { FavoriteButton } from "components/elements/FavoriteButton";
import { TrendingLink } from "components/elements/TrendingLink";
import type { RouterOutputs } from "lib/trpc";
import { trpc } from "lib/trpc";

type TrendingItem = RouterOutputs["favorites"]["getTrending"][number];

function getTrendingTitle(item: TrendingItem): string {
    switch (item.type) {
        case "vendor":
            return item.data.name;
        default:
            return item.data.title ?? "Untitled";
    }
}

interface TrendingSidebarProps {
    filterType?: FavoriteTargetType;
    title?: string;
    limit?: number;
}

export const TrendingSidebar = ({
    filterType,
    title = "Trending",
    limit = 5,
}: TrendingSidebarProps) => {
    const { data, isLoading } = trpc.favorites.getTrending.useQuery({
        limit,
        sinceDays: 7,
        filterType,
    });

    if (isLoading) {
        return (
            <div className="rounded-2xl border border-[#c8c8c8] bg-white p-4">
                <div className="mb-3 flex items-center gap-2">
                    <TrendingUp className="h-4 w-4 text-orange-500" />
                    <h3 className="font-sans text-[16px] font-semibold text-[#424242]">{title}</h3>
                </div>
                <div className="space-y-3">
                    {[1, 2, 3].map((i) => (
                        <div key={i} className="h-4 animate-pulse rounded bg-gray-100" />
                    ))}
                </div>
            </div>
        );
    }

    if (!data || data.length === 0) return null;

    return (
        <div className="rounded-2xl border border-[#c8c8c8] bg-white p-4">
            <div className="mb-3 flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-orange-500" />
                <h3 className="font-sans text-[16px] font-semibold text-[#424242]">{title}</h3>
            </div>
            <div className="space-y-2">
                {data.map((item, index) => {
                    const id = item.data.id;
                    return (
                        <TrendingLink
                            key={`${item.type}:${id}`}
                            type={item.type}
                            id={id}
                            className="block no-underline"
                        >
                            <div className="flex items-start gap-2 rounded-lg p-2 transition-colors hover:bg-gray-50">
                                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-orange-100 text-[10px] font-bold text-orange-600">
                                    {index + 1}
                                </span>
                                <div className="min-w-0 flex-1">
                                    <p className="line-clamp-2 text-sm font-medium text-gray-900">
                                        {getTrendingTitle(item)}
                                    </p>
                                    <p className="text-xs text-gray-400">Score: {item.score}</p>
                                </div>
                                <FavoriteButton
                                    target={buildFavoriteTarget(item.type, id)}
                                    size={12}
                                />
                            </div>
                        </TrendingLink>
                    );
                })}
            </div>
        </div>
    );
};
